import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import AdminLayout from '@/components/AdminLayout';
function formatDate(value) {
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));
}
export default function AdminMarketing() {
    const navigate = useNavigate();
    const { user, role, loading: authLoading } = useAuth();
    const [signups, setSignups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!user)
            return;
        async function loadSignups() {
            setLoading(true);
            setError(null);
            const { data, error: loadError } = await supabase
                .from('newsletter_signups')
                .select('id, email, source, ip_address, user_agent, created_at')
                .order('created_at', { ascending: false });
            if (loadError) {
                setError(loadError.message);
                setSignups([]);
            }
            else {
                setSignups(data ?? []);
            }
            setLoading(false);
        }
        void loadSignups();
    }, [user]);
    const filteredSignups = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (!term)
            return signups;
        return signups.filter((signup) => signup.email.toLowerCase().includes(term) ||
            signup.source.toLowerCase().includes(term));
    }, [query, signups]);
    async function handleDelete(signup) {
        if (!confirm(`Remove ${signup.email} from newsletter signups?`))
            return;
        setDeletingId(signup.id);
        setError(null);
        const { error: deleteError } = await supabase
            .from('newsletter_signups')
            .delete()
            .eq('id', signup.id);
        setDeletingId(null);
        if (deleteError) {
            setError(deleteError.message);
            return;
        }
        setSignups((prev) => prev.filter((row) => row.id !== signup.id));
    }
    if (authLoading || loading) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center", children: _jsx("p", { className: "text-gray-500", children: "Loading\u2026" }) }));
    }
    if (!user || role !== 'admin') {
        navigate('/', { replace: true });
        return null;
    }
    return (_jsx(AdminLayout, { children: _jsxs("main", { className: "max-w-6xl mx-auto px-4 py-8 space-y-6", children: [_jsxs("div", { className: "flex flex-col gap-3 md:flex-row md:items-end md:justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900", children: "Marketing" }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: "Newsletter signups captured from the Springfield Dog Centre website." })] }), _jsxs("div", { className: "bg-white border border-gray-200 rounded-xl px-4 py-3 min-w-[220px]", children: [_jsx("p", { className: "text-xs uppercase tracking-widest text-gray-400 mb-1", children: "Total signups" }), _jsx("p", { className: "text-2xl font-semibold text-brand-ink", children: signups.length })] })] }), _jsxs("section", { className: "bg-white border border-gray-200 rounded-xl p-4", children: [_jsxs("div", { className: "flex flex-col gap-4 md:flex-row md:items-center md:justify-between", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-sm font-semibold text-brand-ink", children: "Newsletter contacts" }), _jsx("p", { className: "text-sm text-gray-500", children: "Search, review, and remove newsletter signups." })] }), _jsx("input", { type: "search", value: query, onChange: (e) => setQuery(e.target.value), placeholder: "Search by email or source", className: "w-full md:w-72 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30" })] }), error && (_jsx("div", { className: "mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700", children: error })), _jsxs("div", { className: "mt-4 overflow-hidden rounded-xl border border-gray-200", children: [_jsxs("div", { className: "hidden md:grid grid-cols-[minmax(0,1.4fr)_160px_180px_120px] gap-4 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500", children: [_jsx("span", { children: "Email" }), _jsx("span", { children: "Source" }), _jsx("span", { children: "Signed up" }), _jsx("span", { children: "Actions" })] }), filteredSignups.length === 0 ? (_jsx("div", { className: "px-4 py-8 text-sm text-gray-400 text-center", children: "No newsletter signups found." })) : (_jsx("ul", { className: "divide-y divide-gray-100", children: filteredSignups.map((signup) => (_jsx("li", { className: "px-4 py-4", children: _jsxs("div", { className: "grid gap-3 md:grid-cols-[minmax(0,1.4fr)_160px_180px_120px] md:items-center", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-brand-ink truncate", children: signup.email }), _jsxs("p", { className: "text-xs text-gray-400 mt-1 md:hidden", children: [signup.source, " \u00B7 ", formatDate(signup.created_at)] })] }), _jsx("p", { className: "hidden md:block text-sm text-gray-600", children: signup.source }), _jsx("p", { className: "hidden md:block text-sm text-gray-600", children: formatDate(signup.created_at) }), _jsx("div", { className: "flex items-center gap-3", children: _jsx("button", { onClick: () => handleDelete(signup), disabled: deletingId === signup.id, className: "text-sm text-red-600 hover:underline disabled:opacity-50", children: deletingId === signup.id ? 'Removing…' : 'Remove' }) })] }) }, signup.id))) }))] })] })] }) }));
}
